import { Alert, Button, Card, Grid, Group, Stack, Text, Textarea, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconArrowBackUp, IconCalendarQuestion, IconCircleCheck, IconHourglass, IconSend } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router';
import type { FileRefDto } from '@mexp/shared';
import { http } from '@/api/client';
import { useCurrentUser } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState, LoadingState } from '@/components/States';
import { FileList } from '@/features/files/FileList';
import { FileUploader } from '@/features/files/FileUploader';
import { departmentName, useDepartments } from '@/features/shared/lookups';
import { useTask, useTaskTimeline } from '@/features/requests/api';
import { DueDate, TaskStatusBadge } from '@/features/requests/components/badges';
import { EventTimeline } from '@/features/requests/components/EventTimeline';
import { TaskVersions } from '@/features/requests/components/TaskVersions';
import { useDrAction } from '@/features/requests/mutations';
import { formatBusinessDate, fromNow, toBusinessDate } from '@/lib/format';

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text fz="sm" c="dimmed">
        {label}
      </Text>
      <Text fz="sm" ta="right">
        {children || '—'}
      </Text>
    </Group>
  );
}

export function TaskPage() {
  const { id = '' } = useParams();
  const me = useCurrentUser();
  const task = useTask(id);
  const timeline = useTaskTimeline(id);
  const departments = useDepartments();
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposed, setProposed] = useState<string | null>(null);
  const [proposalNote, setProposalNote] = useState('');

  const submit = useDrAction(
    () => http.post<{ resubmission: boolean }>(`/work-items/${id}/submissions`, { fileIds: files.map((f) => f.id), note: note.trim() || undefined }),
    'Sent to RA for verification',
    () => {
      setFiles([]);
      setNote('');
    },
  );
  const propose = useDrAction(() => http.post(`/work-items/${id}/target-proposal`, { date: proposed, note: proposalNote.trim() || undefined }), 'Date proposed to RA', () => {
    setProposed(null);
    setProposalNote('');
  });

  if (task.isLoading) return <LoadingState />;
  if (task.isError || !task.data) return <ErrorState error={task.error} onRetry={() => void task.refetch()} />;
  const { item, request, permissions } = task.data;
  const mine = item.assignee.id === me.id;
  const checklistItem = request.requiredDocs.find((d) => d.key === item.requiredDocKey);

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'My tasks', to: '/internal/my-tasks' }, { label: item.code }]}
        title={item.title}
        badge={<TaskStatusBadge status={item.status} size="lg" />}
        description={`${request.code} · ${request.productName} for ${request.country} · ${request.exporterName}`}
      />

      <Stack gap="lg">
        {item.status === 'returned' && item.lastReturn && (
          <Alert color="orange" variant="light" icon={<IconArrowBackUp size={20} />} title={`Changes requested by ${item.lastReturn.returnedBy.name} · ${fromNow(item.lastReturn.returnedAt)}`}>
            <Stack gap="xs">
              <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {item.lastReturn.reason}
              </Text>
              {item.lastReturn.files.length > 0 && <FileList files={item.lastReturn.files} />}
              <Text fz="sm" fw={500}>
                Upload the corrected documents below.
              </Text>
            </Stack>
          </Alert>
        )}
        {item.status === 'submitted' && (
          <Alert color="yellow" variant="light" icon={<IconHourglass size={20} />} title="Waiting for RA to verify">
            Submitted {fromNow(item.lastSubmittedAt)}. You can still add files if something was missing.
          </Alert>
        )}
        {item.status === 'verified' && (
          <Alert color="teal" variant="light" icon={<IconCircleCheck size={20} />} title={`Verified by ${item.verifiedBy?.name} · ${fromNow(item.verifiedAt)}`}>
            {item.verificationNote ?? 'Nothing more is needed from you on this task.'}
          </Alert>
        )}
        {item.status === 'cancelled' && (
          <Alert color="gray" variant="light" title="This task was removed">
            {item.cancelReason}
          </Alert>
        )}
        {request.bdStatus === 'pending_review' && item.status !== 'verified' && (
          <Alert color="violet" variant="light">
            The dossier is with BD for review, so uploads are paused.
          </Alert>
        )}

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="lg">
              {permissions.canSubmit && (
                <Card>
                  <Stack gap="md">
                    <Title order={4}>{item.status === 'returned' ? 'Upload corrected documents' : item.status === 'submitted' ? 'Add more files' : 'Upload documents'}</Title>
                    {!mine && (
                      <Alert variant="light" color="gray">
                        You are uploading on behalf of {item.assignee.name}.
                      </Alert>
                    )}
                    <FileUploader area="wi_submission" value={files} onChange={setFiles} onBusyChange={setBusy} />
                    <Textarea label="Note for RA (optional)" autosize minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />
                    <Group justify="flex-end">
                      <Button leftSection={<IconSend size={16} />} disabled={!files.length || busy} loading={submit.isPending} onClick={() => submit.mutate()}>
                        {busy ? 'Waiting for uploads…' : 'Submit for verification'}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              )}

              <Card>
                <Stack gap="md">
                  <Title order={4}>{item.queryId ? 'Compliance' : 'Versions'}</Title>
                  <TaskVersions item={item} onBehalfLabel={mine ? ' (on your behalf)' : undefined} />
                </Stack>
              </Card>

              <Card>
                <Stack gap="md">
                  <Title order={4}>History</Title>
                  {timeline.data ? <EventTimeline events={timeline.data} /> : <Text c="dimmed" fz="sm">Loading…</Text>}
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="lg">
              <Card>
                <Stack gap="sm">
                  <Title order={4}>What's needed</Title>
                  <Row label="Target date">
                    <DueDate date={item.targetDate} open={item.status === 'assigned' || item.status === 'returned'} />
                  </Row>
                  <Row label="Department">{departmentName(departments.data, item.department)}</Row>
                  {checklistItem && <Row label="Checklist item">{checklistItem.name}</Row>}
                  {item.instructions && (
                    <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {item.instructions}
                    </Text>
                  )}
                  {item.attachments.length > 0 && (
                    <>
                      <Text fz="sm" fw={500}>
                        Reference files from RA
                      </Text>
                      <FileList files={item.attachments} />
                    </>
                  )}
                </Stack>
              </Card>

              {item.targetProposal ? (
                <Alert color="yellow" variant="light" icon={<IconCalendarQuestion size={18} />} title={`Proposed ${formatBusinessDate(item.targetProposal.date)}`}>
                  Waiting for RA to accept the date.
                </Alert>
              ) : (
                permissions.canProposeTarget && (
                  <Card>
                    <Stack gap="sm">
                      <Title order={5}>Propose a target date</Title>
                      <Text fz="sm" c="dimmed">
                        No date has been set. Tell RA when you can deliver.
                      </Text>
                      <DateInput value={proposed} onChange={(v) => setProposed(toBusinessDate(v))} minDate={new Date()} valueFormat="DD MMM YYYY" placeholder="Choose a date" />
                      <Textarea placeholder="Reason or dependency (optional)" autosize minRows={1} value={proposalNote} onChange={(e) => setProposalNote(e.currentTarget.value)} />
                      <Button variant="light" disabled={!proposed} loading={propose.isPending} onClick={() => propose.mutate()}>
                        Propose date
                      </Button>
                    </Stack>
                  </Card>
                )
              )}

              <Card>
                <Stack gap="sm">
                  <Title order={4}>About the dossier</Title>
                  <Row label="Exporter">{request.exporterName}</Row>
                  <Row label="Country">{request.country}</Row>
                  <Row label="Brand / Generic name">{request.productName}</Row>
                  <Row label="Pack size">{request.packSize}</Row>
                  <Row label="Category">{request.category}</Row>
                  <Row label="Dosage form">{request.dosageForm}</Row>
                  {request.referenceFiles.length > 0 && (
                    <>
                      <Text fz="sm" fw={500} mt="xs">
                        Reference files
                      </Text>
                      <FileList files={request.referenceFiles} />
                    </>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
}
